/**
 * httpProgress — mini store singleton para o TopProgressBar global.
 *
 * Ago/2026 — Ricardo: "cada vez que o site cria algo ou faz algum loading,
 * coloca barra de progresso". Solução:
 *
 *   1. Um contador de pedidos activos (`pending`).
 *   2. Chamadas `start()` incrementam, `done()` decrementam.
 *   3. Enquanto `pending > 0` a barra fica visível.
 *   4. Quando volta a 0, faz snap para 100% e desaparece.
 *
 * Uso indirecto (automático):
 *   - Registamos interceptors de axios em `App.js` que chamam start/done.
 *   - Monkey-patch de `window.fetch` no boot da app faz o mesmo para fetch().
 *
 * Uso directo (manual — para trabalho síncrono pesado, ex: gerar blobs):
 *   import { setDeterminate } from '@/lib/httpProgress';
 *   setDeterminate(true, 25); // barra a 25%
 *   setDeterminate(true, 100);
 *   setDeterminate(false);
 */

const listeners = new Set();
let state = {
  pending: 0,        // nº de pedidos activos
  determinate: false, // se true → mostra `percent` real; se false → animação indefinida
  percent: 0,        // 0-100 (só usado quando determinate)
  visible: false,
};

function emit() {
  listeners.forEach((fn) => {
    try { fn(state); } catch (_) { /* ignora */ }
  });
}

export function subscribe(fn) {
  listeners.add(fn);
  fn(state);
  return () => listeners.delete(fn);
}

export function getState() {
  return state;
}

export function start() {
  state = { ...state, pending: state.pending + 1, visible: true };
  emit();
}

export function done() {
  const next = Math.max(0, state.pending - 1);
  state = { ...state, pending: next, visible: next > 0 || state.determinate };
  emit();
}

/** Reset forçado — usado em rotas que sabem que não há mais requests. */
export function reset() {
  state = { pending: 0, determinate: false, percent: 0, visible: false };
  emit();
}

/**
 * Modo determinado (progresso real). Ex: gerar blob localmente sem HTTP.
 *   setDeterminate(true, 42) → mostra barra a 42%
 *   setDeterminate(false)   → sai do modo determinado
 */
export function setDeterminate(active, percent = 0) {
  if (active) {
    state = {
      ...state,
      determinate: true,
      percent: Math.max(0, Math.min(100, percent)),
      visible: true,
    };
  } else {
    state = {
      ...state,
      determinate: false,
      percent: 0,
      visible: state.pending > 0,
    };
  }
  emit();
}
